import type { OrderPartialCancellation } from '@skupreme/api-contracts/core';

/* Matches the neighbouring "Partially shipped" badge, which this one sits beside. */
export const PARTIALLY_CANCELED_LABEL = 'Partially canceled';

export type CanceledLineRow = {
  key: string;
  /** The line's title, or its SKU when the marketplace sent no title. */
  title: string;
  sku: string;
  qty: number;
  /** The marketplace's refund note, where one was stored. */
  note?: string;
};

/**
 * The removed lines of a partly canceled order, ready for the hover. The API
 * only sends the field when at least one line was removed and one was not, so
 * an empty result means "no badge", never "a badge with nothing to say".
 */
export function getCanceledLines(
  cancellation: OrderPartialCancellation | undefined,
): CanceledLineRow[] {
  return (cancellation?.items || [])
    .filter(item => item && Number(item.qty_canceled) > 0)
    .map((item, index) => {
      const sku = item.sku?.trim() || '';
      const title = item.title?.trim() || sku || 'Item';
      const note = item.note?.trim();

      return {
        key: `${item.item_id || 'line'}-${index}`,
        title,
        sku,
        qty: Number(item.qty_canceled),
        ...(note ? { note } : {}),
      };
    });
}

/** "1 line removed" / "3 lines removed, 5 units", for the hover's first row. */
export function summarizeCanceledLines(lines: CanceledLineRow[]): string {
  const units = lines.reduce((total, line) => total + line.qty, 0);
  const head = `${lines.length} ${lines.length === 1 ? 'line' : 'lines'} removed`;

  return units === lines.length ? head : `${head}, ${units} units`;
}
